import { requiredValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import { RelationshipConfig } from "config/config";
import { executionLocationField } from "config/resources/sharedFieldsTyped";
import {
    DeleteListAction,
    deleteActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import { isEqual } from "lodash";
import { isEnabledField } from "./sharedFields";

const pluginActionVariables = `
    $parent_plugin_action_id: String,
    $record_type: RecordType,
    $name: String,
    $description: String,
    $icon: String,
    $color: String,
    $is_enabled: Boolean,
    $flow_id: String,
    $record_filter: SchemalessData
    $execution_location: String
    $navigate_to_execution: Boolean
    $roles: SchemalessData
`;
const pluginActionQuery = `
    parent_plugin_action_id: $parent_plugin_action_id,
    record_type: $record_type,
    name: $name,
    description: $description,
    icon: $icon,
    color: $color,
    is_enabled: $is_enabled,
    flow_id: $flow_id,
    record_filter: $record_filter
    execution_location: $execution_location
    navigate_to_execution: $navigate_to_execution
    roles: $roles
`;
const config: RelationshipConfig = {
    label: "Plugin action",
    icon: IconCollection.cubeSend,
    resourceName: "plugin_action",
    resourceClass: "relationship",
    parentResourceName: "plugin",
    parentReferenceField: "plugin_id",
    parentReferenceName: "plugin_id_plugin.name",
    layout: [],
    hideCreateNew: true,
    labelDocs: {
        title: "Plugin action",
        urlPath: "/docs/plugins#configuration",
    },
    actions: {
        shown: [],
        hidden: [deleteActionSignature],
    },
    listActions: {
        shown: [],
        hidden: [{ key: "delete", action: DeleteListAction }],
    },
    graphql: {
        resource: "plugin_action",
        resourceBody: `
            id
            plugin_id
            plugin_id_plugin{
                name
            }
            parent_plugin_action_id
            record_type
            name
            description
            icon
            color
            is_enabled
            flow_id
            type
            record_filter
            execution_location
            navigate_to_execution
            roles
        `,
        resourceBodyListing: `
            id
            plugin_id
            record_type
            name
        `,
        createVariables: `
            $plugin_id: String
            $type: PluginActionType,
            ${pluginActionVariables}
        `,
        createQuery: `
            plugin_id: $plugin_id
            type: $type,
            ${pluginActionQuery}
        `,
        updateVariables: `
            $id: String!,
            $by: String,
            ${pluginActionVariables}
        `,
        updateQuery: `
            ${pluginActionQuery}
        `,
    },
    form: [
        {
            name: "name",
            component: "text",
            fieldProps: {
                label: "Name",
            },
            fieldAttr: {
                required: true,
            },
            validation: requiredValidator,
        },
        {
            name: "description",
            component: "text",
            fieldProps: {
                label: "Description",
            },
        },
        executionLocationField({
            optionList: [
                { value: "triggering_record", label: "Triggering record" },
                { value: "plugin_resource", label: "Plugin" },
            ],
        }),
        {
            name: "navigate_to_execution",
            component: "switch",
            fieldProps: {
                label: "Navigate to execution",
                fieldNote: () =>
                    "When on (default), clicking the action opens the created " +
                    "execution. When off, you stay on the current screen and a " +
                    "success notification with a link to the execution is shown.",
            },
        },
        {
            name: "roles",
            component: "roleNames",
            fieldProps: {
                label: "Roles",
                fieldNote: () =>
                    "Roles the execution runs with. Leave empty to use the roles of " +
                    "the user who clicks the action that are set to propagate. Each listed role must be assigned " +
                    "to the user who clicks the action, with or without propagate.",
            },
            isEqual: (a: any, b: any) => isEqual(a ?? null, b ?? null),
        },
        {
            name: "parent_plugin_action_id",
            component: "autocomplete",
            fieldProps: {
                label: "Parent action",
                options: {
                    resource: "plugin_action",
                },
            },
        },
        {
            name: "record_type",
            component: "autocomplete",
            fieldProps: {
                label: "Record type",
                options: {
                    enumName: "RecordType",
                },
            },
        },
        {
            // Curated brand-colour picker (no free-text hex). Each option maps to a
            // canonical button treatment resolved at render time in
            // PluginListAction.jsx: default->dark (peer), action->primary (blue),
            // danger->danger, success/warning->plugin-action-scoped fill. Keeping the
            // set closed keeps plugin-action colours inside the semantic palette
            // (ui-design-principle-color-palette) and the button-variant model
            // (ui-design-principle-buttons). Curated set approved by Margot 2026-09-28.
            name: "color",
            component: "autocomplete",
            fieldProps: {
                label: "Color",
                options: {
                    list: [
                        { label: "Default", value: "default" },
                        { label: "Action", value: "action" },
                        { label: "Danger", value: "danger" },
                        { label: "Success", value: "success" },
                        { label: "Warning", value: "warning" },
                    ],
                },
            },
        },
        {
            name: "icon",
            component: "autocomplete",
            fieldProps: {
                label: "Icon",
                options: {
                    list: Object.keys(IconCollection).map((key) => ({
                        label: key,
                        value: key,
                        iconName: key,
                    })),
                },
            },
        },
        isEnabledField,
        {
            name: "flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "Flow",
                options: {
                    resource: "flow",
                },
            },
        },
        {
            name: "record_filter",
            // Advanced-Search filter builder form controls (RecordFilterBuilderFormComponent),
            // scoped to the action's record_type / target object_template. Stores the
            // record_filter grammar object directly (SchemalessData), so no YAML
            // (de)serialisation — the field value is the same shape the client-side
            // visibility evaluator (utils/recordFilter.ts) consumes.
            component: "recordFilterBuilder",
            fieldProps: {
                label: "Record filter",
            },
            isEqual: (a: any, b: any) => isEqual(a, b),
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            // record_filter is stored as the grammar object and edited by the filter
            // builder directly — pass it through untransformed.
            const execution_location =
                data?.execution_location === "triggering_resource"
                    ? "triggering_record"
                    : data?.execution_location;
            // Mirror the backend server_default (true) so a new/legacy action
            // shows the toggle ON rather than an ambiguous unset (off) state.
            const navigate_to_execution = data?.navigate_to_execution ?? true;
            return { ...data, execution_location, navigate_to_execution };
        },
    },
};
export default config;
